import { memo } from 'react';
import type { QualityProfile } from '@cyphex/types';
import type { ConnectionQuality } from '../../media/contract';
import { QualityIndicator } from '../QualityIndicator';
import { SettingRow, ToggleRow } from './SettingRow';

export interface NetworkTabProps {
  quality: ConnectionQuality;
  profile: QualityProfile;
  downlinkBps: number;
  packetLoss: number;
  lowBandwidth: boolean;
  degradedReason: string | null;
  onLowBandwidthChange: (on: boolean) => void;
}

const PROFILE_LABEL: Readonly<Record<QualityProfile, string>> = {
  standard: 'Standard — up to 720p',
  dense: 'Dense room — up to 360p',
  'low-bandwidth': 'Low bandwidth — 180p',
  'audio-only': 'Audio only — no video received',
};

const mbps = (bps: number): string => `${(bps / 1_000_000).toFixed(2)} Mbps`;

/**
 * What the connection is doing and the one lever the user owns over it.
 *
 * Low-bandwidth mode is a FLOOR, not a suggestion: the automatic ladder can
 * still go lower on a bad line, but it can never climb back above 180p while
 * this is on — which is exactly what someone on a metered link wants.
 */
export const NetworkTab = memo(function NetworkTab(props: NetworkTabProps) {
  return (
    <div className="flex flex-col divide-y divide-border">
      <ToggleRow
        label="Low-bandwidth mode"
        hint="Caps every incoming video at 180p and reduces screen-share quality. Best on mobile data."
        checked={props.lowBandwidth}
        onChange={props.onLowBandwidthChange}
      />

      <SettingRow label="Connection" hint={props.degradedReason ?? 'Measured every two seconds.'}>
        <QualityIndicator quality={props.quality} size="md" />
      </SettingRow>

      <SettingRow label="Current quality profile">
        <span className="text-md text-muted-foreground">{PROFILE_LABEL[props.profile]}</span>
      </SettingRow>

      <SettingRow label="Incoming video">
        <span className="text-md tabular text-muted-foreground">{mbps(props.downlinkBps)}</span>
      </SettingRow>

      <SettingRow label="Packet loss">
        <span className="text-md tabular text-muted-foreground">
          {(props.packetLoss * 100).toFixed(2)}%
        </span>
      </SettingRow>
    </div>
  );
});
